import { useEffect, useState, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Icon } from "@majourneys/icons";
import { buildRefLink, paysByLink } from "@majourneys/shared";
import { formatNumber } from "../../../lib/format";
import { useDisplayPriceParts } from "../../../lib/currency";
import { SITE_ORIGIN } from "../../../shared/seo/page-seo";
import { panelStyle } from "../../../shared/ui/card-frame";
import { activitiesApi } from "../../activities/api/activities.api";
import { activityHrefString } from "../../activities/lib/activity-href";
import {
  CONTROL_EDGE,
  CONTROL_FILL,
  GOLD_DARK,
  GOLD_TINT,
  GOLD,
  HAIRLINE,
  MUTED,
  NAVY,
  NAVY_GRADIENT,
  PRIMARY_SHADOW,
  Panel,
  initialOf,
  primaryClass,
  primaryStyle,
  CHIP_ON,
  NAVY_FACE,
  WHITE_FACE,
  WHITE_INSET,
} from "./kit";

/* ------------------------------------------------------------------ */
/* Places you could share (owner, 2026-10-10, suggestion 86)          */
/* ------------------------------------------------------------------ */

type ListItem = {
  id: string;
  slug: string;
  title: string;
  segment: string | null;
  images?: string[] | null;
  price?: number | null;
  category?: { name: string; slug: string } | null;
  city?: { name: string; slug: string } | null;
};

/** Kinds of listing in the program; transfers, nightlife, stays and camps are not. */
function shareable(x: ListItem): boolean {
  if (x.category?.slug === "transport") return false;
  if (paysByLink(x.slug)) return false;
  return x.segment === "activities" || x.segment === "restaurants";
}

/**
 * Four real listings, one of each kind where the catalogue has it: an
 * activity, a restaurant, a spa, then another activity. Exported for tests.
 */
export function pickShowcase(items: ListItem[]): ListItem[] {
  const ok = items.filter((x) => shareable(x) && (x.images?.length ?? 0) > 0);
  const isSpa = (x: ListItem) => x.category?.slug === "spas";
  const isRestaurant = (x: ListItem) => x.segment === "restaurants" || x.category?.slug === "restaurants";
  const isActivity = (x: ListItem) => !isSpa(x) && !isRestaurant(x);
  const picks: ListItem[] = [];
  for (const test of [isActivity, isRestaurant, isSpa, isActivity]) {
    const next = ok.find((x) => test(x) && !picks.includes(x));
    if (next) picks.push(next);
  }
  return picks;
}

/** The real listings this page shows; one request shared by every section. */
function useShowcase(lang: string): ListItem[] {
  const list = useQuery({
    queryKey: ["ambassadors", "showcase", lang],
    queryFn: () => activitiesApi.list({ city: "marrakech", mode: "bookable", page_size: "24", lang }),
    staleTime: 10 * 60_000,
  });
  return pickShowcase(((list.data as { data?: ListItem[] } | undefined)?.data ?? []) as ListItem[]);
}

export function SharePlaces({ lang, handle, onApply }: { lang: string; handle: string; onApply: () => void }) {
  const { t } = useTranslation();
  const [made, setMade] = useState<string | null>(null);
  const picks = useShowcase(lang);
  if (picks.length === 0) return null;
  const madeItem = picks.find((p) => p.id === made);
  const link = madeItem
    ? buildRefLink(
        SITE_ORIGIN,
        activityHrefString({ slug: madeItem.slug, lang, citySlug: madeItem.city?.slug, categorySlug: madeItem.category?.slug, segment: madeItem.segment ?? undefined }),
        handle || t("ambassadors.join.card_handle_empty"),
      )
    : null;

  return (
    <section className="mx-auto max-w-[1180px] px-5 pt-16" aria-labelledby="amb-places-title" data-testid="share-places">
      <h2 id="amb-places-title" className="text-center font-sans text-[26px] font-semibold sm:text-[32px]" style={{ color: NAVY, letterSpacing: "-0.03em" }}>
        {t("ambassadors.join.places_title")}
      </h2>
      <p className="mx-auto mt-2 max-w-[52ch] text-center font-sans text-[15px]" style={{ color: MUTED }}>
        {t("ambassadors.join.places_sub")}
      </p>
      <ul className="mt-8 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        {picks.map((p) => (
          <li key={p.id} className="flex min-w-0 flex-col gap-2">
            {/* A plain card, not the booking card: this page sells sharing, not the listing. */}
            <a
              href={activityHrefString({ slug: p.slug, lang, citySlug: p.city?.slug, categorySlug: p.category?.slug, segment: p.segment ?? undefined })}
              className="group flex min-w-0 flex-col overflow-hidden p-1.5"
              style={panelStyle(20)}
            >
              <img src={p.images?.[0]} alt="" loading="lazy" decoding="async" className="aspect-[4/3] w-full rounded-[14px] object-cover" />
              <span className="px-1.5 pb-1.5 pt-2">
                {p.category?.name ? (
                  <span className="block break-words font-sans text-[11px] font-bold uppercase" style={{ color: GOLD_DARK, letterSpacing: "0.06em" }}>
                    {p.category.name}
                  </span>
                ) : null}
                <span className="line-clamp-2 block font-sans text-[14px] font-semibold leading-snug" style={{ color: NAVY }}>
                  {p.title}
                </span>
              </span>
            </a>
            <button
              type="button"
              onClick={() => setMade(p.id)}
              aria-pressed={made === p.id}
              className="mj-press inline-flex min-h-[40px] items-center justify-center gap-1.5 rounded-full font-sans text-[13px] font-semibold"
              style={
                made === p.id
                  ? { background: NAVY_FACE, color: "#fff", boxShadow: PRIMARY_SHADOW }
                  : { background: WHITE_FACE, color: NAVY, boxShadow: `${WHITE_INSET}, 0 0 0 1.5px ${CONTROL_EDGE}` }
              }
              data-testid="make-link"
            >
              <Icon name={made === p.id ? "ri-check-line" : "ri-link"} className="text-[15px]" aria-hidden />
              {t("ambassadors.join.make_link")}
            </button>
          </li>
        ))}
      </ul>
      {link && (
        /* The link it would make, with their handle from the hero; a real one comes once approved. */
        <div key={link} className="mj-rise-in mx-auto mt-5 flex max-w-[640px] flex-col items-center gap-2 text-center" aria-live="polite" data-testid="made-link">
          {/* clamp-ok: link URL */}
          <p className="max-w-full truncate rounded-full px-4 py-2 font-sans text-[13px]" style={{ background: CONTROL_FILL, boxShadow: `0 0 0 1px ${CONTROL_EDGE}`, color: NAVY }} dir="ltr">
            {link.replace(/^https?:\/\//, "")}
          </p>
          <p className="font-sans text-[12.5px]" style={{ color: MUTED }}>
            {t("ambassadors.join.link_once_approved")}{" "}
            <button type="button" onClick={onApply} className="font-semibold underline" style={{ color: NAVY }}>
              {t("ambassadors.join.hero_cta")}
            </button>
          </p>
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* A look at the dashboard (suggestion 87)                            */
/* ------------------------------------------------------------------ */

/**
 * What "See what works" means: clicks and bookings per link, marked as an
 * example. No earnings figure, so the page never hints at the share.
 */
export function SampleDashboard({ lang }: { lang: string }) {
  const { t } = useTranslation();
  const rows = [
    { key: "a", clicks: 214, bookings: 3 },
    { key: "b", clicks: 96, bookings: 2 },
    { key: "c", clicks: 61, bookings: 1 },
  ];
  const n = (v: number) => formatNumber(v, lang);
  return (
    <section className="mx-auto max-w-[1180px] px-5 pt-16" aria-labelledby="amb-dash-title" data-testid="sample-dashboard">
      <div className="grid items-center gap-8 lg:grid-cols-2">
        <div className="min-w-0 text-center lg:text-start">
          <h2 id="amb-dash-title" className="font-sans text-[26px] font-semibold sm:text-[32px]" style={{ color: NAVY, letterSpacing: "-0.03em" }}>
            {t("ambassadors.join.benefit_stats_title")}
          </h2>
          <p className="mx-auto mt-2 max-w-[44ch] font-sans text-[15px] lg:mx-0" style={{ color: MUTED }}>
            {t("ambassadors.join.dash_sub")}
          </p>
        </div>
        <Panel className="mx-auto w-full max-w-[480px] p-5" radius={24}>
          <div className="flex items-center justify-between gap-3">
            <p className="font-sans text-[15px] font-semibold" style={{ color: NAVY }}>
              {t("ambassadors.join.dash_month")}
            </p>
            <span className="rounded-full px-2.5 py-0.5 font-sans text-[11px] font-bold uppercase" style={{ background: GOLD_TINT, color: GOLD_DARK, letterSpacing: "0.06em" }}>
              {t("ambassadors.join.dash_example")}
            </span>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2.5">
            {[
              { icon: "ri-cursor-line", label: t("ambassadors.join.dash_clicks"), value: 371 },
              { icon: "ri-shopping-bag-3-line", label: t("ambassadors.join.dash_bookings"), value: 6 },
            ].map((s) => (
              <div key={s.label} className="rounded-[16px] p-3.5" style={{ background: CONTROL_FILL, boxShadow: `0 0 0 1px ${CONTROL_EDGE}` }}>
                <span className="flex items-center gap-1.5 font-sans text-[12px] font-semibold" style={{ color: MUTED }}>
                  <Icon name={s.icon} className="text-[14px]" aria-hidden />
                  {s.label}
                </span>
                <span className="font-sans text-[24px] font-semibold tabular-nums" style={{ color: NAVY }}>
                  {n(s.value)}
                </span>
              </div>
            ))}
          </div>
          <table className="mt-4 w-full font-sans text-[13px]" style={{ color: NAVY }}>
            <thead>
              <tr style={{ color: MUTED }}>
                <th className="pb-1.5 text-start text-[11.5px] font-semibold">{t("ambassadors.join.dash_link")}</th>
                <th className="pb-1.5 text-end text-[11.5px] font-semibold">{t("ambassadors.join.dash_clicks")}</th>
                <th className="pb-1.5 text-end text-[11.5px] font-semibold">{t("ambassadors.join.dash_bookings")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} style={{ borderTop: `1px solid ${HAIRLINE}` }}>
                  <td className="py-2 font-semibold">{t(`ambassadors.join.dash_row_${r.key}`)}</td>
                  <td className="py-2 text-end tabular-nums" style={{ color: MUTED }}>
                    {n(r.clicks)}
                  </td>
                  <td className="py-2 text-end font-semibold tabular-nums">{n(r.bookings)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* A look at their own page (owner, 2026-10-13, "A")                  */
/* ------------------------------------------------------------------ */

/**
 * What "Your own page" means: a phone showing a small copy of the real
 * /a/<handle> page (profile-page.tsx), with the handle typed in the hero and
 * three real listings. Marked as an example; nothing in it is a link.
 */
export function SamplePage({ lang, handle }: { lang: string; handle: string }) {
  const { t } = useTranslation();
  const displayPrice = useDisplayPriceParts();
  const picks = useShowcase(lang).slice(0, 3);
  if (picks.length === 0) return null;
  const name = handle || t("ambassadors.join.card_handle_empty");
  // Two chips at most, so the row fits the phone instead of running off it.
  const cats = ([...new Set(picks.map((p) => p.category?.name).filter(Boolean))] as string[]).slice(0, 2);
  const chip = "inline-flex shrink-0 items-center whitespace-nowrap rounded-full px-2.5 py-1 font-sans text-[11px] font-semibold";

  return (
    <section className="mx-auto max-w-[1180px] px-5 pt-16" aria-labelledby="amb-page-title" data-testid="sample-page">
      <div className="grid items-center gap-8 lg:grid-cols-2">
        <div className="min-w-0 text-center lg:order-2 lg:text-start">
          <h2 id="amb-page-title" className="font-sans text-[26px] font-semibold sm:text-[32px]" style={{ color: NAVY, letterSpacing: "-0.03em" }}>
            {t("ambassadors.join.benefit_page_title")}
          </h2>
          <p className="mx-auto mt-2 max-w-[44ch] font-sans text-[15px] lg:mx-0" style={{ color: MUTED }}>
            {t("ambassadors.join.benefit_page_body")}
          </p>
          {/* clamp-ok: page URL */}
          <p className="mt-3 inline-block max-w-full truncate rounded-full px-4 py-2 font-sans text-[13px]" style={{ background: CONTROL_FILL, boxShadow: `0 0 0 1px ${CONTROL_EDGE}`, color: MUTED }} dir="ltr">
            majourneys.com/a/<span style={{ color: NAVY, fontWeight: 600 }}>{name}</span>
          </p>
        </div>

        {/* The phone: a frame, then the page as it reads on a phone. */}
        <figure className="relative mx-auto w-full max-w-[320px] p-2.5 lg:order-1" style={panelStyle(44)} aria-label={t("ambassadors.join.page_preview_label")}>
          <span className="absolute end-5 top-5 z-[1] rounded-full px-2.5 py-0.5 font-sans text-[11px] font-bold uppercase" style={{ background: GOLD_TINT, color: GOLD_DARK, letterSpacing: "0.06em" }}>
            {t("ambassadors.join.dash_example")}
          </span>
          <div className="overflow-hidden rounded-[36px] bg-white px-4 pb-5 pt-8" aria-hidden>
            <div className="flex flex-col items-center text-center">
              <span className="grid h-16 w-16 place-items-center rounded-full font-sans text-[24px] font-semibold" style={{ background: "#F8EEDC", color: GOLD_DARK }}>
                {initialOf(name)}
              </span>
              {/* clamp-ok: guest's own handle */}
              <p className="mt-2 max-w-full truncate font-sans text-[17px] font-semibold" style={{ color: NAVY, letterSpacing: "-0.02em" }} dir="ltr">
                @{name}
              </p>
              <span className="mt-1.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-sans text-[9.5px] font-bold uppercase" style={{ background: GOLD_TINT, color: GOLD_DARK, letterSpacing: "0.08em" }}>
                <Icon name="ri-shield-check-line" className="text-[11px]" />
                {t("ambassadors.badge")}
              </span>
            </div>
            <div className="mt-4 flex justify-center gap-1.5">
              <span className={chip} style={{ background: NAVY_FACE, color: "#fff", boxShadow: CHIP_ON }}>
                <Icon name="ri-check-line" className="me-1 text-[12px]" style={{ color: GOLD }} />
                {t("ambassadors.profile.all")} · {formatNumber(picks.length, lang)}
              </span>
              {cats.map((c) => (
                <span key={c} className={chip} style={{ background: WHITE_FACE, color: NAVY, boxShadow: `${WHITE_INSET}, 0 0 0 1px ${CONTROL_EDGE}` }}>
                  {c}
                </span>
              ))}
            </div>
            <ul className="mt-3 flex flex-col gap-2">
              {picks.map((p) => {
                const restaurant = p.segment === "restaurants" || p.category?.slug === "restaurants";
                const price = !restaurant && p.price && p.price > 0 ? displayPrice(p.price) : null;
                const line = restaurant
                  ? t("ambassadors.profile.book_table")
                  : price
                    ? t("ambassadors.profile.from_price", { price: `${price.value} ${price.currency}` })
                    : null;
                return (
                  <li key={p.id} className="flex min-w-0 items-center gap-2.5 rounded-[16px] p-1.5 pe-2" style={{ background: CONTROL_FILL, boxShadow: `0 0 0 1px ${CONTROL_EDGE}` }}>
                    <img src={p.images?.[0]} alt="" loading="lazy" decoding="async" className="h-11 w-11 shrink-0 rounded-[11px] object-cover" />
                    <span className="min-w-0 flex-1 text-start">
                      <span className="line-clamp-2 block font-sans text-[12.5px] font-semibold leading-snug" style={{ color: NAVY }}>{p.title}</span>
                      <span className="line-clamp-2 block font-sans text-[11px]" style={{ color: MUTED }}>
                        {[p.category?.name, line].filter(Boolean).join(" · ")}
                      </span>
                    </span>
                    <Icon name="ri-arrow-right-s-line" className="shrink-0 text-[16px] rtl:rotate-180" style={{ color: MUTED }} />
                  </li>
                );
              })}
            </ul>
            <p className="mt-3 text-center font-sans text-[10.5px]" style={{ color: MUTED }}>
              {t("ambassadors.profile.pay_note")}
            </p>
          </div>
        </figure>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Apply, always in reach on a phone (suggestion 85)                  */
/* ------------------------------------------------------------------ */

/**
 * A slim bar pinned to the bottom of a phone once the hero's Apply has
 * scrolled away, gone again while the form is on screen. Slides in and out
 * (transform only). Wider screens keep the hero card in view and get none.
 */
export function StickyApply({
  heroRef,
  formRef,
  onApply,
  hidden,
}: {
  heroRef: RefObject<HTMLElement | null>;
  formRef: RefObject<HTMLElement | null>;
  onApply: () => void;
  /** Already an ambassador, or applied: nothing to apply for. */
  hidden: boolean;
}) {
  const { t, i18n } = useTranslation();
  const forward = i18n.language === "ar" ? "ri-arrow-left-line" : "ri-arrow-right-line";
  const [heroSeen, setHeroSeen] = useState(true);
  const [formSeen, setFormSeen] = useState(false);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return undefined;
    const watch = (el: Element | null | undefined, set: (v: boolean) => void) => {
      if (!el) return null;
      const io = new IntersectionObserver(([e]) => set(Boolean(e?.isIntersecting)), { threshold: 0 });
      io.observe(el);
      return io;
    };
    const a = watch(heroRef.current, setHeroSeen);
    const b = watch(formRef.current, setFormSeen);
    return () => {
      a?.disconnect();
      b?.disconnect();
    };
  }, [heroRef, formRef]);

  const show = !hidden && !heroSeen && !formSeen;
  return (
    <div
      className="fixed inset-x-0 bottom-0 z-40 px-3 pb-[max(12px,env(safe-area-inset-bottom))] transition-transform duration-300 motion-reduce:transition-none lg:hidden"
      style={{ transform: show ? "translateY(0)" : "translateY(130%)", transitionTimingFunction: "var(--mj-ease-out)" }}
      aria-hidden={!show}
      inert={!show}
      data-testid="sticky-apply"
      data-shown={show ? "true" : "false"}
    >
      <div className="mx-auto flex max-w-[560px] items-center gap-3 rounded-[26px] p-2 ps-4" style={{ background: "rgba(255,255,255,0.96)", boxShadow: "0 18px 44px -18px rgba(17,14,57,0.45), 0 0 0 1px rgba(17,14,57,0.08)" }}>
        <div className="min-w-0 flex-1">
          <p className="break-words font-sans text-[14px] font-semibold" style={{ color: NAVY }}>
            {t("ambassadors.join.sticky_title")}
          </p>
          <p className="line-clamp-2 font-sans text-[12px]" style={{ color: MUTED }}>
            {t("ambassadors.join.hero_note")}
          </p>
        </div>
        <button type="button" onClick={onApply} className={primaryClass("md")} style={primaryStyle}>
          {t("ambassadors.join.hero_cta")}
          <Icon name={forward} className="text-[15px]" aria-hidden />
        </button>
      </div>
    </div>
  );
}
